import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { debounceTime, filter, fromEvent, merge, Subject, Subscription } from 'rxjs';

import { LoggerService } from 'app/services/logger.service';
import { Utils } from 'app/utils/utils';
import {
  ActivityBarInteraction,
  DashboardPillComponent,
} from '../dashboard-pill/dashboard-pill.component';
import { AppSharedModule } from '../../app-shared.module';
import { WidgetDataSource } from '../../models';
import { ComparisonHandlerService } from '../../modules/dashboard/services/comparison-handler-service';
import { DatasourceManagerStore } from '../../modules/dashboard/stores/datasource-manager.store';
import {
  WidgetGroupStore,
  isDashboardLevelWidgetGroup,
} from '../../modules/dashboard/stores/widget-group.store';
import { WidgetComponent } from '../../modules/widget/components/widget/widget.component';
import { AbstractChartReport } from '../../modules/widget/services/chart-report/chart-report-abstract/chart-report-abstract';
import {
  Comparison,
  Dashboard,
  Widget,
  WidgetGroup,
} from '../../providers/bridge-api/dashboard.service';
import { PillHoverDirective, PillHoverState } from './pill-hover.directive';

export interface WidgetPillHoverState {
  entity: WidgetDataSource | WidgetGroup | Comparison;
  isHovered: boolean;
}

enum CondenseLevel {
  NONE,
  DATASOURCE,
  WIDGET_GROUP,
  COMPARISON,
}

@Component({
  selector: 'app-dashboard-pill-wrapper',
  templateUrl: './dashboard-pill-wrapper.component.html',
  styleUrls: ['./dashboard-pill-wrapper.component.scss'],
  imports: [AppSharedModule, DashboardPillComponent, PillHoverDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardPillWrapperComponent implements OnChanges, OnInit, AfterViewInit, OnDestroy {
  @Input() currentPageId: number;
  @Input() disabled = false;
  @Input() dashboardId: string;
  @Input() currentWidgets: Widget[];
  @Input() activityParent: HTMLDivElement;
  @Input() containerParent: HTMLDivElement;
  @Input() widgetAddBtn: HTMLDivElement;
  @Input() widget?: Widget;
  @Input() report?: AbstractChartReport;
  @Input() dashboard?: Dashboard;
  @Input() detailsHidden: boolean;
  @Input() widgetComponent?: WidgetComponent;
  @Input() beforeCompact?: (...args) => number;
  @Input() afterCompact?: (...args) => number;
  @Input() isReadOnly = false;

  @Output() onActivitybarInteraction: EventEmitter<any> = new EventEmitter();
  @Output() pillHover = new EventEmitter<PillHoverState>();

  private readonly datasourceManagerStore = inject(DatasourceManagerStore);
  private comparisonService: ComparisonHandlerService = inject(ComparisonHandlerService);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private destroy = inject(DestroyRef);
  private widgetGroupStore = inject(WidgetGroupStore);
  private logger = inject(LoggerService);

  isPillCompact = false;
  dataSourceData: {
    source?: WidgetDataSource[];
    activeWidgetCount?: { [key: string]: number };
  } = {
    activeWidgetCount: {},
  };
  comparisons: Comparison[] = [];
  allComparisonsForPage: Comparison[] = [];
  private comparisonSub: Subscription;

  /** Mirror signals kept in sync with @Input via ngOnChanges. */
  private currentPageId$ = signal<number>(undefined);
  private currentWidgets$ = signal<Widget[]>([]);
  private widget$ = signal<Widget | undefined>(undefined);

  allWidgetGroupsForPage = computed(() => {
    const pageId = this.currentPageId$();
    if (!pageId) {
      return [];
    }
    return this.widgetGroupStore.getByPage(pageId);
  });

  widgetGroups = computed(() => {
    const allGroups = this.allWidgetGroupsForPage();
    const currentWidgets = this.currentWidgets$();
    const currentPageId = this.currentPageId$();
    const widget = this.widget$();
    let filtered = allGroups.filter((group) => {
      if (isDashboardLevelWidgetGroup(group)) {
        return group.widgets.some(
          (widgetId) =>
            currentWidgets?.find((w) => w.id === widgetId)?.payload.page === currentPageId
        );
      }
      return group.widgets?.length || group.parentWidget;
    });
    if (widget) {
      filtered = filtered.filter(
        (group) => group.widgets.includes(widget.id) || group.parentWidget === widget.id
      );
    }
    return this.sortArray<WidgetGroup>(filtered, 'title').map((a) => ({ ...a }));
  });

  isDestroyed;

  checkCompactingSubject = new Subject<void>();
  isCheckCompacting = signal(false);
  condenseLevel = signal<CondenseLevel>(CondenseLevel.COMPARISON);
  isDatasourceCondensed = computed(() => this.condenseLevel() >= CondenseLevel.DATASOURCE);
  isWidgetGroupCondensed = computed(() => this.condenseLevel() >= CondenseLevel.WIDGET_GROUP);
  isComparisonCondensed = computed(() => this.condenseLevel() >= CondenseLevel.COMPARISON);

  lastCondenseOperation;
  isViewInitialized;
  isEntityChooserVisible;

  constructor() {
    this.checkCompactingSubject
      .pipe(
        filter(() => !this.isCheckCompacting() && !this.isDestroyed),
        takeUntilDestroyed(),
        debounceTime(50)
      )
      .subscribe(() => this.checkCompactSize());

    fromEvent(window, 'resize')
      .pipe(takeUntilDestroyed(), debounceTime(300))
      .subscribe(() => this.checkCompactingSubject.next());

    toObservable(this.widgetGroups)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.checkCompactingSubject.next());

    this.datasourceManagerStore.widgetMapChange$
      .pipe(
        takeUntilDestroyed(),
        filter(
          (res) =>
            this.widget?.id === res?.widgetId ||
            this.widget?.dataSourceId === res?.dataSourceId ||
            this.currentWidgets?.some((w) => w.id === res?.widgetId)
        )
      )
      .subscribe((res) => this.getActiveDataSourcesForActiveWidgets(res));
  }

  ngOnInit(): void {
    this.comparisonSub?.unsubscribe();
    this.comparisonSub = this.subscribeToComparisonChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.currentWidgets?.currentValue) {
      this.getActiveDataSourcesForActiveWidgets();
    }

    if (changes.currentPageId) {
      this.currentPageId$.set(changes.currentPageId.currentValue);
      if (changes.currentPageId.currentValue !== changes.currentPageId.previousValue) {
        this.condenseLevel.set(CondenseLevel.COMPARISON);
      }
    }
    if (changes.currentWidgets) {
      this.currentWidgets$.set(changes.currentWidgets.currentValue);
    }
    if (changes.widget) {
      this.widget$.set(changes.widget.currentValue);
    }
  }

  ngAfterViewInit(): void {
    this.isViewInitialized = true;
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
  }

  private subscribeToComparisonChanges(): Subscription {
    return merge(
      this.comparisonService.comparisonListChanged$,
      this.comparisonService.comparisonApplied$,
      this.comparisonService.comparisonRevoked$
    )
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((res) => {
        let needRecalculation = false;
        if (Array.isArray(res)) {
          needRecalculation = this.handleComparisonListChange(res);
        } else if (res.hasOwnProperty('comparison')) {
          needRecalculation = this.handleComparisonApply(
            res as { comparison: Comparison; widgetId: string }
          );
        } else if (res.hasOwnProperty('revokedComparison')) {
          needRecalculation = this.handleComparisonRevoke(
            res as { widgetId: string; revokedComparison: Comparison }
          );
        }

        this.comparisons = this.sortArray<Comparison>(this.comparisons, 'title');

        if (needRecalculation) {
          this.checkCompactingSubject.next();
        }
      });
  }

  private handleComparisonListChange(res: Comparison[]): boolean {
    this.allComparisonsForPage = [...res];
    let activeComparisons = res.filter((comp) => comp.widgets.length > 0);

    if (this.widget) {
      activeComparisons = activeComparisons.filter((c) => c.widgets.includes(this.widget.id));
    }
    const needRecalculation = !Utils.deepEquals(this.comparisons, activeComparisons);
    this.comparisons = activeComparisons;

    return needRecalculation;
  }

  private handleComparisonApply(serviceResult: {
    comparison: Comparison;
    widgetId: string;
  }): boolean {
    if (this.widget && this.widget.id !== serviceResult.widgetId) {
      return;
    }

    this.allComparisonsForPage.find((c) => c.id === serviceResult.comparison.id).widgets = [
      ...serviceResult.comparison.widgets,
    ];

    const newComparisonIndex = this.comparisons.findIndex(
      (c) => c.id === serviceResult.comparison.id
    );

    this.comparisons.splice(
      newComparisonIndex,
      newComparisonIndex < 0 ? 0 : 1,
      serviceResult.comparison
    );

    this.comparisons = this.comparisons.map((a) => ({
      ...a,
    }));

    return newComparisonIndex === -1;
  }

  private handleComparisonRevoke(serviceResult: {
    widgetId: string;
    revokedComparison: Comparison;
  }): boolean {
    if (this.widget && this.widget.id !== serviceResult.widgetId) {
      return false;
    }

    this.allComparisonsForPage.find((c) => c.id === serviceResult.revokedComparison.id).widgets = [
      ...serviceResult.revokedComparison.widgets,
    ];

    this.comparisons.find((c) => c.id === serviceResult.revokedComparison.id).widgets = [
      ...serviceResult.revokedComparison.widgets,
    ];

    const oldComparisonLength = this.comparisons.length;

    this.comparisons = this.comparisons
      .filter((c) => c.widgets.length)
      .map((a) => ({
        ...a,
      }));

    if (this.widget) {
      this.comparisons = [];
    }

    return oldComparisonLength !== this.comparisons.length;
  }

  private getActiveDataSourcesForActiveWidgets(change?: {
    action: 'REMOVE' | 'ADD';
    dataSourceId: string;
    widgetId: string;
  }): void {
    this.dataSourceData = {
      activeWidgetCount: {},
    };

    const activeWidgetsWithDataSource = this.currentWidgets
      ?.filter(
        (w) =>
          w?.payload?.type !== 'note' &&
          w?.payload?.type !== 'noteMarkdown' &&
          w?.payload?.type !== 'insightFeed'
      )
      .map((widget) => widget.dataSourceId);
    const activeDataSources = Array.from(new Set(activeWidgetsWithDataSource));

    this.dataSourceData.source = this.sortArray<WidgetDataSource>(
      this.datasourceManagerStore.getActiveDataSources(activeDataSources),
      'collectionDisplayName'
    );

    this.dataSourceData.source.forEach((ds) => {
      if (ds?.id) {
        const activeWidgetCount = this.datasourceManagerStore.dataSourceWidgetMap()[ds.id];
        this.dataSourceData.activeWidgetCount[ds.id] = activeWidgetCount?.length ?? 1;
      }
    });

    if (!change || !this.widget || this.widget.id === change.widgetId) {
      this.checkCompactingSubject.next();
    }
  }

  get maxCondenseLevel() {
    if (this.comparisons?.length) {
      return CondenseLevel.COMPARISON;
    }
    if (this.widgetGroups()?.length) {
      return CondenseLevel.WIDGET_GROUP;
    }
    return CondenseLevel.DATASOURCE;
  }

  checkCompactSize(previousWidthsPerLevel?) {
    if (
      (this.isCheckCompacting() && !previousWidthsPerLevel) ||
      !this.isViewInitialized ||
      !this.containerParent ||
      this.isEntityChooserVisible ||
      this.isDestroyed ||
      this.disabled
    ) {
      return;
    }

    this.cdr.detach();

    this.isCheckCompacting.set(true);
    const subHeaderWidthsPerLevel = previousWidthsPerLevel ?? {
      [CondenseLevel.NONE]: 0,
      [CondenseLevel.DATASOURCE]: 0,
      [CondenseLevel.WIDGET_GROUP]: 0,
      [CondenseLevel.COMPARISON]: 0,
    };
    const parentPadding = parseInt(
      window.getComputedStyle(this.activityParent).paddingLeft ?? '15',
      10
    );
    const parentWidth = this.activityParent.offsetWidth;

    this.beforeCompact?.({ parentWidth, parentPadding });

    this.cdr.detectChanges();

    const widgetSubHeaderWidth = this.containerParent.scrollWidth + 2 * parentPadding;
    subHeaderWidthsPerLevel[this.condenseLevel()] = widgetSubHeaderWidth;
    if (widgetSubHeaderWidth > parentWidth && this.condenseLevel() < this.maxCondenseLevel) {
      this.condenseLevel.update((level) => level + 1);
      this.lastCondenseOperation = 'contract';
      this.cdr.detectChanges();
      this.logger.info('#checkCompactSize CONTRACTING...', {
        widget: this.widget ? `${this.widget.component} - ${this.widget.id}` : 'Dashboard',
        level: this.condenseLevel(),
        widgetSubHeaderWidth,
        parentWidth,
      });
      this.checkCompactSize(subHeaderWidthsPerLevel);
      return;
    } else if (widgetSubHeaderWidth <= parentWidth && this.condenseLevel() > CondenseLevel.NONE) {
      if (
        this.lastCondenseOperation !== 'contract' &&
        subHeaderWidthsPerLevel[this.condenseLevel() - 1] <= parentWidth
      ) {
        this.condenseLevel.update((level) => level - 1);
        this.lastCondenseOperation = 'expand';
        this.cdr.detectChanges();
        this.logger.info('#checkCompactSize EXPANDING...', {
          widget: this.widget ? `${this.widget.component} - ${this.widget.id}` : 'Dashboard',
          level: this.condenseLevel(),
        });
        this.checkCompactSize(subHeaderWidthsPerLevel);
        return;
      }
    }

    this.lastCondenseOperation = undefined;

    this.afterCompact?.({ parentWidth, parentPadding });

    this.isCheckCompacting.set(false);
    this.cdr.reattach();
  }

  async onInteraction(
    interactionEvent: ActivityBarInteraction<WidgetGroup | WidgetDataSource | Comparison>
  ) {
    if (this.dashboard) {
      interactionEvent.dashboard = this.dashboard;
    }
    if (this.widget) {
      interactionEvent.widget = this.widget;
    }
    this.onActivitybarInteraction.emit(interactionEvent);
  }

  private sortArray<T>(array: Array<T>, sortKey: keyof T): T[] {
    return array.sort((a, b) => {
      const aPrimary = (a[sortKey] as string) || '';
      const bPrimary = (b[sortKey] as string) || '';
      return aPrimary.localeCompare(bPrimary, undefined, {});
    });
  }

  onMouseMovement(pillHoverState: PillHoverState) {
    this.pillHover.emit(pillHoverState);
  }
}
